import * as React from 'react';

import PropTypes from 'prop-types';
import { branch, compose } from 'recompose';

import createLoadableComponent from 'js/lib/createLoadableComponent';

import type { PropsFromWithProductFeatures } from 'bundles/admin-dashboard/components/AdminDashboardPage';
import type { ThirdPartyOrganization } from 'bundles/admin-dashboard/types';
import enterpriseAdminUser from 'bundles/enterprise-admin';
import { gwgContractTags } from 'bundles/enterprise-admin-constants/EnterpriseContractConstants';
import { handleRedirect } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/utils';
import type { PropsFromWithGoogleCertificates } from 'bundles/enterprise-admin-grow-with-google/components/withGoogleCertificates';
import withGoogleCertificates from 'bundles/enterprise-admin-grow-with-google/components/withGoogleCertificates';
import { includesTargetContract } from 'bundles/enterprise-admin-grow-with-google/utils/helpers';
import withEnterpriseAdminRolePermissionsRequired from 'bundles/enterprise-admin-roles/components/withEnterpriseAdminRolePermissionsRequired';
import type { EnterpriseContractsV1 } from 'bundles/naptimejs/resources/__generated__/EnterpriseContractsV1';
import type EnterpriseAdminRolesV1 from 'bundles/naptimejs/resources/enterpriseAdminRoles.v1';

const GrowWithGoogleSetupContainer = createLoadableComponent(
  () => import('bundles/enterprise-admin-grow-with-google/components/GrowWithGoogleSetupContainer')
);
const InviteLearnersFlowContainer = createLoadableComponent(
  () => import('bundles/enterprise-admin-grow-with-google/components/InviteLearnersFlowContainer')
);
export const GWG_PROGRAM_SETUP_PATH = 'gwg-program-setup';
export const GWG_CONTINUE_PROGRAM_SETUP_PATH = 'gwg-continue-program-setup';
export const GWG_INVITE_LEARNERS = 'gwg-invite-learners';

type SecondaryOrgTypeProps = Partial<
  Pick<PropsFromWithProductFeatures, 'isC4CBOrg' | 'isGwGOrg' | 'isGwGFreemiumOrg' | 'isGwGPaidOrg'>
>;

type PropsFromCaller = {
  contracts: Array<EnterpriseContractsV1>;
  thirdPartyOrganization: ThirdPartyOrganization;
  enterpriseAdminRoles: Array<EnterpriseAdminRolesV1>;
};

export type Props = PropsFromCaller & PropsFromWithGoogleCertificates & SecondaryOrgTypeProps;

const withGrowWithGoogleSetup = (Component: React.ComponentType) => {
  class GrowWithGoogleSetup extends React.Component<Props> {
    static contextTypes = {
      router: PropTypes.object.isRequired,
    };

    constructor(props: Props) {
      super(props);
      const { enterpriseAdminRoles } = props;
      enterpriseAdminUser.setRoles(enterpriseAdminRoles);
    }

    render() {
      const { router } = this.context;
      const { thirdPartySlug, programSlug } = router.params;
      const { thirdPartyOrganization, gwgCertificates, gwgLanguages, isGwGOrg, isGwGFreemiumOrg, isGwGPaidOrg } =
        this.props;
      const currentPath = router.routes[router.routes.length - 1].path;
      const isGwGSetupPath = currentPath === GWG_PROGRAM_SETUP_PATH;
      const isGwGContinueProgramSetupPath = currentPath === GWG_CONTINUE_PROGRAM_SETUP_PATH;
      const isGwGFreemiumInviteLearnersPath = currentPath === GWG_INVITE_LEARNERS;

      const enableGwGSetup = Boolean(isGwGOrg);
      const enableGwGFreemiumSetup = Boolean(isGwGFreemiumOrg);
      const enableGwGPaidSetup = Boolean(isGwGPaidOrg);

      if (isGwGSetupPath || isGwGContinueProgramSetupPath || isGwGFreemiumInviteLearnersPath || isGwGPaidOrg) {
        if (!(enableGwGSetup || enableGwGFreemiumSetup || enableGwGPaidSetup)) {
          handleRedirect(`/o/${thirdPartySlug}/admin/home`, router);
          return null;
        }

        if (isGwGSetupPath) {
          return (
            <GrowWithGoogleSetupContainer
              orgId={thirdPartyOrganization.id}
              orgSlug={thirdPartySlug}
              programSlug={programSlug}
              isMergedOrg={enableGwGFreemiumSetup || enableGwGPaidSetup}
              router={router}
              gwgCertificates={gwgCertificates}
              gwgLanguages={gwgLanguages}
              isProgramCreation
            />
          );
        }

        if (isGwGContinueProgramSetupPath) {
          return (
            <GrowWithGoogleSetupContainer
              orgId={thirdPartyOrganization.id}
              orgSlug={thirdPartySlug}
              isMergedOrg={enableGwGFreemiumSetup || enableGwGPaidSetup}
              router={router}
              programSlug={programSlug}
              gwgCertificates={gwgCertificates}
              gwgLanguages={gwgLanguages}
            />
          );
        }

        if (isGwGFreemiumInviteLearnersPath) {
          return (
            <InviteLearnersFlowContainer
              orgSlug={thirdPartySlug}
              orgId={thirdPartyOrganization.id}
              programSlug={programSlug}
              isMergedOrg={enableGwGFreemiumSetup || enableGwGPaidSetup}
              gwgCertificates={gwgCertificates}
              gwgLanguages={gwgLanguages}
            />
          );
        }
      }

      return <Component {...this.props} />;
    }
  }

  const enhanced = compose<Props, PropsFromCaller>(
    withEnterpriseAdminRolePermissionsRequired,
    branch<Props>(
      ({ contracts }) =>
        includesTargetContract({
          contracts,
          target: gwgContractTags,
          skipBillingCheck: true,
        }),
      withGoogleCertificates()
    )
  )(GrowWithGoogleSetup);

  return enhanced;
};

export default withGrowWithGoogleSetup;
